import React, { useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Result, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { fetchPublicForm, fetchReceipt, submitApplication } from '../public-form/api'
import { ApplicationInput, Receipt } from '../public-form/public-form'
import { PublicFormPage } from '../public-form/components/public-form-page'
import { isAxiosError } from 'axios'

export const Route = createFileRoute('/apply/$publicId')({ component: ApplicationPage })
function ApplicationPage() {
    const { publicId } = Route.useParams()
    const { t } = useTranslation()
    const idempotency = useRef(crypto.randomUUID())
    const [receipt, setReceipt] = useState<Receipt>()
    const query = useQuery({
        queryKey: ['public-form', publicId],
        queryFn: () => fetchPublicForm(publicId),
        retry: false,
        staleTime: 0,
    })
    const mutation = useMutation({
        mutationFn: (input: Omit<ApplicationInput, 'idempotencyKey' | 'formVersion'>) =>
            submitApplication(publicId, {
                ...input,
                idempotencyKey: idempotency.current,
                formVersion: query.data!.version,
            }),
        onSuccess: setReceipt,
    })
    const status = useQuery({
        queryKey: ['public-submission', publicId, receipt?.receipt],
        queryFn: () => fetchReceipt(publicId, receipt!.receipt),
        enabled: receipt?.state === 'pending',
        refetchInterval: (q) => (q.state.data?.state === 'pending' || !q.state.data ? 3000 : false),
        retry: false,
    })
    if (query.isPending)
        return (
            <div className="p-10 text-center">
                <Spin />
            </div>
        )
    if (query.isError)
        return (
            <Result
                status="404"
                title={t('Form unavailable')}
                subTitle={t('This application link is unavailable.')}
                extra={<Button onClick={() => void query.refetch()}>{t('Retry')}</Button>}
            />
        )
    const lng = query.data.configuration.locale
    const error = mutation.isError
        ? t(
              isAxiosError(mutation.error) && mutation.error.response?.data?.errors?.turnstile_token
                  ? 'Verification failed. Please retry the security check.'
                  : isAxiosError(mutation.error) && mutation.error.response?.status === 429
                    ? 'You are sending too many requests, wait a bit and try again'
                    : isAxiosError(mutation.error) && mutation.error.response?.status === 410
                      ? 'Applications are closed'
                      : isAxiosError(mutation.error) && mutation.error.response?.status === 409
                        ? 'The form has changed. Reload it before submitting again.'
                        : 'Check your details, including the country code, and try again. If the problem continues, contact the company.',
              { lng }
          )
        : undefined
    return (
        <PublicFormPage
            configuration={query.data.configuration}
            turnstileSiteKey={query.data.turnstileSiteKey}
            available={query.data.available}
            pending={mutation.isPending}
            receipt={status.data ?? receipt}
            error={error}
            onReload={
                isAxiosError(mutation.error) && mutation.error.response?.status === 409
                    ? () => {
                          idempotency.current = crypto.randomUUID()
                          mutation.reset()
                          void query.refetch()
                      }
                    : undefined
            }
            onSubmit={async (input) => {
                await mutation.mutateAsync(input).catch(() => undefined)
            }}
        />
    )
}
